/**
 * @fileoverview "Detalhes" da abordagem inteligente: quem iniciou, cada
 * mensagem com a hora e a entrega (enviada, entregue, lida, falhou), quando o
 * cliente respondeu e por que parou. Relê a abordagem ao abrir (a entrega muda
 * sem aviso da abordagem).
 */

import React, { useEffect } from "react";
import Dialog from "@material-ui/core/Dialog";
import clsx from "clsx";
import { X } from "lucide-react";

import useTriageStyles from "../Triage/styles";
import { formatDateTime } from "../Triage/helpers";
import { runView, stepLines } from "../../utils/smartApproachText";
import useStyles from "./styles";
import { formatAt, tr } from "./text";

const SmartApproachDetails = ({ run, open, onClose, onReload, onCancel }) => {
  const classes = useStyles();
  const timeline = useTriageStyles();

  useEffect(() => {
    if (open) onReload();
  }, [open, onReload]);

  if (!run) return null;
  const now = Date.now();
  const view = runView(run, { now, t: tr, formatAt });
  const lines = stepLines(run, { now, t: tr, formatAt });
  const replyAfter = run.firstReplyAt ? run.repliedAfterStep || run.lastSentStep || 1 : null;
  const events = lines.flatMap((line) => {
    const meta = [line.status, line.at ? formatDateTime(line.at) : null].filter(Boolean).join(" · ");
    const event = {
      key: `step-${line.step}`,
      tone: line.tone,
      title: line.templateName ? `${line.label} · ${line.templateName}` : line.label,
      meta,
      error: line.error,
    };
    if (line.step !== replyAfter) return [event];
    const reply = { key: "reply", tone: "ok", title: tr("details.reply"), meta: formatDateTime(run.firstReplyAt) };
    return [event, reply];
  });
  const started = run.startedBy
    ? tr("details.startedBy", { name: run.startedBy, date: formatDateTime(run.startedAt) })
    : tr("details.startedAt", { date: formatDateTime(run.startedAt) });

  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ className: classes.paper }} aria-labelledby="smart-approach-details-title">
      <div className={classes.header}>
        <div className={classes.headerText}>
          <h2 id="smart-approach-details-title" className={classes.dialogTitle}>
            {tr("details.title")}
          </h2>
          {run.flowName && <div className={classes.subtitle}>{run.flowName}</div>}
        </div>
        <button type="button" className={classes.close} onClick={onClose} aria-label={tr("details.close")}>
          <X />
        </button>
      </div>
      <div className={classes.body}>
        {view && (
          <div className={classes.summary}>
            <span className={classes.title}>{view.title}</span>
            {" · "}
            <span className={clsx(classes.detail, view.tone !== "neutral" && classes[`tone_${view.tone}`])}>{view.detail}</span>
            {run.stopDetail && <span className={classes.stopDetail}>{run.stopDetail}</span>}
          </div>
        )}
        <div className={timeline.timeline}>
          <div className={timeline.event}>
            <span className={timeline.eventLine} />
            <span className={clsx(timeline.eventDot, timeline.dot_neutral)} aria-hidden="true" />
            <div className={timeline.eventBody}>
              <span className={timeline.eventTitle}>{started}</span>
            </div>
          </div>
          {events.map((e, i) => (
            <div key={e.key} className={timeline.event} data-event={e.key}>
              {i < events.length - 1 && <span className={timeline.eventLine} />}
              <span className={clsx(timeline.eventDot, timeline[`dot_${e.tone}`])} aria-hidden="true" />
              <div className={timeline.eventBody}>
                <span className={timeline.eventTitle}>{e.title}</span>
                {e.meta && (
                  <span className={clsx(timeline.eventMeta, e.tone !== "neutral" && classes[`tone_${e.tone}`])}>{e.meta}</span>
                )}
                {e.error && <span className={timeline.eventMeta}>{e.error}</span>}
              </div>
            </div>
          ))}
        </div>
        {view?.phase === "pending" && <p className={classes.footnote}>{tr("bar.hint")}</p>}
      </div>
      <div className={classes.footer}>
        {view?.phase === "pending" && (
          <button type="button" className={classes.btn} onClick={onCancel}>
            {tr("bar.cancel")}
          </button>
        )}
        <button type="button" className={classes.btn} onClick={onClose}>
          {tr("details.close")}
        </button>
      </div>
    </Dialog>
  );
};

export default SmartApproachDetails;
